import { createWidgetSession, type WidgetApi } from "./session.js";

type StoredSession = {
  token: string;
  conversationId: string;
};

const STYLE = `
:host { all: initial; }
* { box-sizing: border-box; font-family: "Segoe UI", system-ui, sans-serif; }
.bubble {
  position: fixed; right: 20px; bottom: 20px; z-index: 2147483646;
  width: 56px; height: 56px; border: 0; border-radius: 50%;
  background: #1f4d3a; color: #f4f1ea; cursor: pointer;
  box-shadow: 0 10px 30px rgba(17, 40, 30, 0.35);
  font-size: 22px;
}
.bubble .dot {
  position: absolute; top: 8px; right: 8px; width: 10px; height: 10px;
  border-radius: 50%; background: #d9c27a;
}
.bubble .dot.on { background: #7dcea0; }
.panel {
  position: fixed; right: 20px; bottom: 88px; z-index: 2147483646;
  width: min(380px, calc(100vw - 24px)); height: min(540px, calc(100vh - 120px));
  background: #f7f3ea; color: #1c2418; border-radius: 18px;
  box-shadow: 0 18px 50px rgba(28, 36, 24, 0.28);
  display: flex; flex-direction: column; overflow: hidden;
}
.header {
  background: #1f4d3a; color: #f7f3ea; padding: 14px 16px;
  display: flex; justify-content: space-between; align-items: flex-start; gap: 8px;
}
.header strong { display: block; font-size: 15px; }
.header p { margin: 4px 0 0; font-size: 12px; opacity: 0.86; }
.header button { background: transparent; color: inherit; border: 0; cursor: pointer; font-size: 18px; }
.body { flex: 1; overflow: auto; padding: 16px; display: flex; flex-direction: column; gap: 12px; }
label { font-size: 12px; font-weight: 600; }
input, textarea {
  width: 100%; border: 1px solid #cfc6b4; background: #fff; color: inherit;
  border-radius: 10px; padding: 10px 12px; font-size: 14px;
}
textarea { min-height: 90px; resize: vertical; }
button.primary {
  background: #1f4d3a; color: #f7f3ea; border: 0; border-radius: 999px;
  padding: 10px 16px; font-weight: 600; cursor: pointer;
}
.msg { max-width: 85%; padding: 8px 10px; border-radius: 12px; font-size: 14px; line-height: 1.4; }
.msg.visitor { align-self: flex-end; background: #1f4d3a; color: #f7f3ea; }
.msg.agent { align-self: flex-start; background: #fff; border: 1px solid #e2d9c8; }
.composer { display: flex; gap: 8px; padding: 12px 16px; border-top: 1px solid #e2d9c8; background: #efe8d8; }
.composer input { flex: 1; }
.error { color: #8c2f2f; font-size: 13px; }
`;

const currentScript = document.currentScript as HTMLScriptElement | null;

function scriptEl(): HTMLScriptElement {
  if (currentScript) {
    return currentScript;
  }
  const scripts = document.querySelectorAll<HTMLScriptElement>("script[data-workspace], script[data-space]");
  return scripts[scripts.length - 1]!;
}

function storageKey(workspaceId: string, spaceId: string) {
  return spaceId ? `codename.chat.${workspaceId}.${spaceId}` : `codename.chat.${workspaceId}`;
}

function loadStored(workspaceId: string, spaceId: string): StoredSession | null {
  const raw = localStorage.getItem(storageKey(workspaceId, spaceId));
  if (!raw) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${storageKey(workspaceId, spaceId)}=([^;]*)`));
    if (!match?.[1]) return null;
    try {
      return JSON.parse(decodeURIComponent(match[1])) as StoredSession;
    } catch {
      return null;
    }
  }
  try {
    return JSON.parse(raw) as StoredSession;
  } catch {
    return null;
  }
}

function persist(workspaceId: string, spaceId: string, value: StoredSession) {
  localStorage.setItem(storageKey(workspaceId, spaceId), JSON.stringify(value));
  document.cookie = `${storageKey(workspaceId, spaceId)}=${encodeURIComponent(JSON.stringify(value))}; path=/; max-age=2592000`;
}

function forget(workspaceId: string, spaceId: string) {
  localStorage.removeItem(storageKey(workspaceId, spaceId));
  document.cookie = `${storageKey(workspaceId, spaceId)}=; path=/; max-age=0`;
}

function createApi(apiBase: string, workspaceId: string, spaceId: string, getToken: () => string | null): WidgetApi & {
  listMessages(): Promise<{ id: string; body: string; authorType: "visitor" | "agent" }[]>;
  connectStream(onEvent: (event: { type?: string; conversationId?: string }) => void): void;
  disconnect(): void;
} {
  let ws: WebSocket | undefined;
  return {
    async getPresence() {
      const params = new URLSearchParams({ workspaceId });
      if (spaceId) params.set("spaceId", spaceId);
      const response = await fetch(`${apiBase}/v1/presence?${params.toString()}`);
      return response.json();
    },
    async getConversation() {
      const stored = loadStored(workspaceId, spaceId);
      const token = getToken();
      if (!stored || !token) return { status: "open" };
      const response = await fetch(`${apiBase}/v1/conversations/${stored.conversationId}`, {
        headers: { authorization: `Bearer ${token}` },
      });
      if (!response.ok) return { status: "open" };
      const body = await response.json();
      return { status: body.conversation?.status === "closed" ? "closed" : "open" };
    },
    async openConversation(email: string) {
      const response = await fetch(`${apiBase}/v1/conversations`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ workspaceId, spaceId, email }),
      });
      const body = await response.json();
      if (!response.ok) {
        throw new Error(body.message ?? "No se pudo abrir el chat");
      }
      persist(workspaceId, spaceId, { token: body.token, conversationId: body.conversation.id });
      return { id: body.conversation.id as string };
    },
    async sendMessage(body: string) {
      const token = getToken();
      const stored = loadStored(workspaceId, spaceId);
      const response = await fetch(
        `${apiBase}/v1/conversations/${stored?.conversationId}/messages`,
        {
          method: "POST",
          headers: {
            "content-type": "application/json",
            authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ body, idempotencyKey: crypto.randomUUID() }),
        },
      );
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const error = new Error(payload.message ?? "No se pudo enviar el mensaje");
        if (payload.error) {
          Object.assign(error, { code: payload.error });
        }
        throw error;
      }
    },
    async listMessages() {
      const stored = loadStored(workspaceId, spaceId);
      const token = getToken();
      if (!stored || !token) return [];
      const response = await fetch(
        `${apiBase}/v1/conversations/${stored.conversationId}/messages`,
        { headers: { authorization: `Bearer ${token}` } },
      );
      if (!response.ok) return [];
      const body = await response.json();
      return body.messages ?? [];
    },
    connectStream(onEvent: (event: { type?: string; conversationId?: string }) => void) {
      const token = getToken();
      if (!token || ws) return;
      const wsBase = apiBase.replace(/^http/, "ws");
      ws = new WebSocket(`${wsBase}/v1/stream?token=${encodeURIComponent(token)}`);
      ws.addEventListener("message", (message) => {
        try {
          onEvent(JSON.parse(String(message.data)));
        } catch {
          onEvent({});
        }
      });
    },
    disconnect() {
      ws?.close();
      ws = undefined;
    },
  };
}

function mount() {
  const script = scriptEl();
  const workspaceId = script.dataset.workspace ?? "";
  const spaceId = script.dataset.space ?? "";
  if (!workspaceId || !spaceId) {
    console.warn("[codename chat] data-workspace and data-space are required");
    return;
  }
  const apiBase = (script.dataset.api ?? new URL(script.src, location.href).origin).replace(/\/$/, "");
  const host = document.createElement("div");
  host.setAttribute("data-codename-chat", "true");
  document.body.appendChild(host);
  const shadow = host.attachShadow({ mode: "open" });
  const wrap = document.createElement("div");
  shadow.appendChild(wrap);

  let token = loadStored(workspaceId, spaceId)?.token ?? null;
  const api = createApi(apiBase, workspaceId, spaceId, () => token);
  const session = createWidgetSession(api);
  let email = "";
  let draft = "";
  let error = "";
  let messages: { id: string; body: string; authorType: "visitor" | "agent" }[] = [];

  async function refreshMessages() {
    messages = await api.listMessages();
    render();
  }

  function onStreamEvent(event: { type?: string; conversationId?: string }) {
    if (
      event.type === "conversation.closed" &&
      (!event.conversationId || event.conversationId === session.conversationId)
    ) {
      session.conversationStatus = "closed";
      if (session.screen === "conversation" || session.screen === "write-message") {
        session.screen = "closed";
      }
      render();
      return;
    }
    void refreshMessages();
  }

  async function boot() {
    try {
      await session.refreshPresence();
      const stored = loadStored(workspaceId, spaceId);
      if (stored) {
        token = stored.token;
        session.conversationId = stored.conversationId;
        await session.restore();
        messages = await api.listMessages();
        api.connectStream(onStreamEvent);
      }
    } catch (err) {
      error = err instanceof Error ? err.message : "No se pudo cargar el chat";
    }
    render();
    setInterval(() => {
      void session.refreshPresence().then(render).catch(() => undefined);
    }, 20_000);
  }

  function render() {
    wrap.innerHTML = "";
    const style = document.createElement("style");
    style.textContent = STYLE;
    wrap.appendChild(style);

    const bubble = document.createElement("button");
    bubble.className = "bubble";
    bubble.type = "button";
    bubble.setAttribute("aria-label", "Abrir chat");
    bubble.textContent = "💬";
    const dot = document.createElement("span");
    dot.className = `dot${session.agentsOnline ? " on" : ""}`;
    bubble.appendChild(dot);
    bubble.addEventListener("click", () => {
      if (session.screen === "bubble") {
        session.open();
      } else {
        session.close();
      }
      render();
    });
    wrap.appendChild(bubble);

    if (session.screen === "bubble") {
      return;
    }

    const panel = document.createElement("section");
    panel.className = "panel";
    const presenceLine = session.agentsOnline
      ? "Hay alguien aquí"
      : "Nadie está ahora";
    const promise = session.agentsOnline
      ? "Normalmente respondemos en unos minutos"
      : "Te responderemos por correo, normalmente en un día";

    panel.innerHTML = `
      <header class="header">
        <div>
          <strong>${presenceLine}</strong>
          <p>${promise}</p>
        </div>
        <button type="button" aria-label="Cerrar">×</button>
      </header>
    `;
    panel.querySelector("button")?.addEventListener("click", () => {
      session.close();
      render();
    });

    const body = document.createElement("div");
    body.className = "body";

    if (session.screen === "ask-email") {
      body.innerHTML = `
        <p>Lo usamos para contactarte si cierras esta ventana.</p>
        <label for="codename-email">Correo</label>
        <input id="codename-email" type="email" placeholder="tu@email.com" value="${email}" />
        ${error ? `<p class="error">${error}</p>` : ""}
        <button class="primary" type="button">Continuar</button>
      `;
      const input = body.querySelector("input") as HTMLInputElement;
      input.addEventListener("input", () => {
        email = input.value;
      });
      body.querySelector("button")?.addEventListener("click", async () => {
        error = "";
        try {
          await session.submitEmail(email);
          token = loadStored(workspaceId, spaceId)?.token ?? null;
          api.disconnect();
          api.connectStream(onStreamEvent);
          await refreshMessages();
        } catch (err) {
          error = err instanceof Error ? err.message : "No se pudo continuar";
        }
        render();
      });
    } else if (session.screen === "write-message") {
      body.innerHTML = `
        <label for="codename-offline">Mensaje</label>
        <textarea id="codename-offline" placeholder="Escribe un mensaje...">${draft}</textarea>
        ${error ? `<p class="error">${error}</p>` : ""}
        <button class="primary" type="button">Enviar</button>
      `;
      const area = body.querySelector("textarea") as HTMLTextAreaElement;
      area.addEventListener("input", () => {
        draft = area.value;
      });
      body.querySelector("button")?.addEventListener("click", async () => {
        error = "";
        try {
          await session.send(draft);
          draft = "";
        } catch (err) {
          error = err instanceof Error ? err.message : "No se pudo enviar";
        }
        render();
      });
    } else if (session.screen === "done") {
      body.innerHTML = `
        <p><strong>Listo, te escribiremos por correo.</strong></p>
        <p>Normalmente en un día. Puedes cerrar esta ventana.</p>
      `;
    } else {
      for (const message of messages) {
        const div = document.createElement("div");
        div.className = `msg ${message.authorType}`;
        div.textContent = message.body;
        body.appendChild(div);
      }
      if (session.screen === "closed") {
        const note = document.createElement("div");
        note.innerHTML = `
          <p><strong>Esta conversación está cerrada.</strong></p>
          <p>Puedes abrir una nueva conversación.</p>
          <button class="primary" type="button">Nueva conversación</button>
        `;
        note.querySelector("button")?.addEventListener("click", () => {
          forget(workspaceId, spaceId);
          token = null;
          api.disconnect();
          messages = [];
          draft = "";
          error = "";
          email = "";
          session.startNew();
          render();
        });
        body.appendChild(note);
      } else if (error) {
        const note = document.createElement("p");
        note.className = "error";
        note.textContent = error;
        body.appendChild(note);
      }
    }

    panel.appendChild(body);

    if (session.screen === "conversation") {
      const composer = document.createElement("form");
      composer.className = "composer";
      composer.innerHTML = `
        <input name="body" placeholder="Escribe un mensaje..." value="${draft}" />
        <button class="primary" type="submit">Enviar</button>
      `;
      const input = composer.querySelector("input") as HTMLInputElement;
      input.addEventListener("input", () => {
        draft = input.value;
      });
      composer.addEventListener("submit", async (event) => {
        event.preventDefault();
        if (!draft.trim()) return;
        error = "";
        try {
          await session.send(draft);
          if (session.screen === "closed") {
            render();
            return;
          }
          draft = "";
          await refreshMessages();
        } catch (err) {
          error = err instanceof Error ? err.message : "No se pudo enviar";
          render();
        }
      });
      panel.appendChild(composer);
    }

    wrap.appendChild(panel);
  }

  void boot();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", mount);
} else {
  mount();
}
